import type { LoaderFunctionArgs } from '~/types/route-args';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactElement } from 'react';
import {
    redirect,
    useLoaderData,
    useLocation,
    useSearchParams,
} from 'react-router';
import invariant from 'tiny-invariant';
import { cn } from '@rocco/utils/cn';
import type { MetaDescriptor } from 'react-router';
import {
    createMetaDescription,
    createMetaTitle,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { fullCountryNameFromCode } from '~/modules/seo/util/localisation';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Box, Container, useStyles } from '@archipro-design/aria';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { DirectoryBrowsePage } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import { useDirectoryDisplayColumns } from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { DirectoryBrowseGrid } from '~/ui-rocco/pages/directory-browse/DirectoryBrowseGrid';
import { resolveContentGate } from '~/modules-rocco/content-gate/resolve-content-gate.server';
import {
    applyContentGate,
    applyContentGateToHits,
    type ContentGateState,
} from '~/modules-rocco/content-gate/content-gate';
import { ContentGatePanel } from '~/modules-rocco/content-gate/ContentGatePanel';
import { DirectoryIntro } from '~/ui-rocco/pages/directory-browse/DirectoryIntro';
import { FadingLogomark } from '~/ui-rocco/pages/products-page/FadingLogomark';
import { ProjectViewPills } from '~/ui-rocco/pages/directory-browse/ProjectViewPills';
import { IMAGE_LABEL_DIRECTORY_FEATURE_KEY } from '@modules/category/util/image-label-directory-flag';
import { GetMatchedHost } from '~/modules-rocco/get-matched/GetMatchedHost';
import {
    getMatchedStyleImages as fetchGetMatchedStyleImages,
    type GetMatchedStyleImage,
} from '~/modules/get-matched/api/get-matched-style-images.server';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import { isCategoryRevampActive } from '@modules/category/util/is-category-revamp-active.server';
import { DIRECTORY_DEFAULT_SORT } from '@modules/category/util/directory-default-sort';
import {
    loadDirectoryBrowse,
    type DirectoryBrowseLoaderData,
} from '@modules/category/util/load-directory-browse.server';
import { PROJECT_FILTERS_CONFIG } from '@modules-rocco/projects/config/filter.config';
import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import type { ProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { mapESProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { getDirectoryPageMeta } from '@modules/category/api';
import { getDirectoryPageMetaCombined } from '~/modules/category/api/get-directory-page-meta-combined.server';
import type { AdsLoaderData } from '@modules/adverts/api';
import { adRequest, adsRequest } from '@modules/adverts/api';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { parseURL } from '@archipro-website/top-navigation';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useDirectoryListImpression } from '~/modules-rocco/directory/use-directory-list-impression';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectCategories } from '~/modules/projects-category/api';
import * as S from '~/modules/projects-category/components/project-category-container/ProjectCategoryContainer.style';
import { advertCreativeProjectTileView } from '@modules/products-category/util/advert-creative-project-tile-view';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getRouteData } from '~/modules/root/util/get-route-data';

import {
    hasFeature,
    useFeature,
    useTileHotButtons,
    type APHandle,
} from '~/modules/root';
import getShowProfessionalArchiproConnectBanner from '~/utils/get-show-professional-archipro-connect-banner';

import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import { professionalEvenShuffleAds } from '~/utils/adHelper';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import {
    isLocationIndexPage,
    noIndexProjectCategory,
} from '@modules/seo/util/no-index-project-category';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProjectsItemListSchema } from '~/modules/seo/schema/projects-item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getHeroPotm } from '@modules/projects-category/api/get-hero-potm.server';
import DirectoryHeroPotmDesktop from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmDesktop';
import { getGridPotms } from '@modules/projects-category/api/get-grid-potms.server';
import { getPreloadImageLink } from '@modules/images';
import DirectoryHeroPotmMobile from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmMobile';
import { usePotmHeroEnabled } from '@modules/projects-category/util/potm-hero-enabled';
import { buildProjectTileGrid } from '~/utils/buildProjectTileGrid';
import { renderProjectTileGrid } from '~/utils/renderProjectTileGrid';
import { getProjectsQuerySize } from '~/utils/get-projects-query-size';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { getProjectsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-project';
import {
    fetchTileWebsites,
    withTileWebsites,
} from '~/modules/directory/util/attach-tile-websites.server';
import { getProjectBannerImages } from '@modules-rocco/projects/api/get-project-banner-images.server';
import { PROJECT_TILE_BANNER_SELECTOR_FLAG } from '~/modules/directory/hook/use-set-item-banner';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import {
    getIsArchiproStaffFromMember,
    getProjectCategoryIgnoreFilterKeys,
    getRedirectUrlWithoutProjectProfessionalsFilter,
    shouldHideUsProjectProfessionalsFilter,
    stripProjectProfessionalsFromSearchFilters,
} from '~/modules/project/util/us-project-professionals-filter-gating';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { parseFooterContentToFAQ } from '~/modules/professionals-category/util/parse-footer-content-to-faq.server';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { getDirectoryFeaturedProjects } from '~/modules/directory/api/get-directory-featured-project.server';
import { useToggleFeaturedProject } from '~/modules/directory/hook/use-toggle-featured-project';
import { useLibraryRemovalUi } from '~/modules/directory/hook/use-library-removal-ui';
import { getProjectCategories as getProjectCategoriesRocco } from '~/modules-rocco/projects/api/get-project-categories.server';
import { parseURL as parseURLRocco } from '@modules-rocco/url/api/parse-url';
import { mapCategoriesToTileViewModel } from '@modules-rocco/categories/data-mappers/category-to-tile';
import { ExploreAllCategories } from '~/ui-rocco/pages/products-page/ExploreAllCategories';
import { TileAppModeScope } from '@rocco/contexts/TileAppModeScopeContext';
import { toast } from 'sonner';

export const PROJECT_TILE_PAGING_WIDTH = 175;
export const PROJECT_TILE_BULLET_SIZE = 11;
export const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
export const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `${data?.directoryPageMeta?.category} projects`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page,
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO',
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Featuring ${
                data?.directoryPageMeta?.category
            } projects from the most talented professionals in ${fullCountryNameFromCode(
                rootData.localisation.country,
            )} with stunning photos and amazing designs to inspire you.`,
        ]);

        const pageUrl = data?.url ?? '';
        const isIndexPage = isLocationIndexPage(pageUrl);
        const projectsListSchema = isIndexPage
            ? createProjectsItemListSchema(props)
            : [];
        const FAQSchema = isIndexPage
            ? createFAQSchema(data?.localisation?.FAQSchemas ?? [])
            : [];
        const ldSchema = createLDSchema([projectsListSchema, FAQSchema]);

        // Preloading disabled - eager loading with fetchPriority="high" provides similar performance benefits
        let preloadImageLinks: MetaDescriptor[] = [];

        if (data?.heroPotm?.videoCoverDesktop) {
            const preloadHero = getPreloadImageLink(
                data.heroPotm.videoCoverDesktop,
                undefined,
                getImagesSizes(1920),
            );
            preloadImageLinks = [...preloadHero, ...preloadImageLinks];
        }

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProjectCategory(metaDescriptors, pageUrl);
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    showStickyNavAfterPosition: 500,
    hideContactArchiPro: false,
    // Big-A unified header (BC-4480) — matches the approved directory mock.
    // The near-black scrolled mobile bar belongs to the dark browse revamp
    // only; the loader's browse branch marks itself with
    // `pageTheme: 'transparent'`, so the legacy light page keeps the default
    // white bar while `revamp_2026` is off.
    header: (loaderData) =>
        (loaderData as { pageTheme?: string } | undefined)?.pageTheme ===
        'transparent'
            ? { showLogomark: true, solidTheme: 'dark' }
            : { showLogomark: true },
};

const DEFAULT_PROJECT_PAGE_SIZE = 36;
const MAX_ADS_PER_PAGE = 3;
const MAX_VERTICAL_TILES_PER_PAGE = 4;
const MAX_VERTICAL_AD_TILES_PER_PAGE = 1;
const MAX_SPONSORED_AD_TILES_DISPLAYED = 3;
const DEFAULT_SORT = DIRECTORY_DEFAULT_SORT.projects;
export const PROJECT_TILE_EXPANDED_WIDTH = 613;
export const PROJECT_TILE_COLLAPSED_WIDTH = 455;

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    // Browse revamp: skip the heavy legacy directory loader and serve the small,
    // self-contained browse payload. Cast to `never` so the browse branch does
    // not widen this loader's type for the legacy directory component.
    if (isCategoryRevampActive(context, request)) {
        // Fetch the browse payload alongside the below-grid content (recently
        // viewed + root categories) ported from the legacy projects page. The
        // featured-projects set loads for every visitor: the pins lead the
        // grid, and for pin admins they also feed the tile context menu's
        // Pin/Unpin label.
        const [
            browseData,
            recentlyViewedResult,
            rootProjectCategoriesResult,
            getMatchedStyleImages,
            featuredProjectsResult,
        ] = await Promise.all([
            loadDirectoryBrowse({
                context,
                request,
                entityType: 'projects',
                config: PROJECT_FILTERS_CONFIG,
                searchIndex: SearchIndexes.Projects,
                eyebrow: 'Projects Library',
                rootPath: '/projects',
            }),
            getRecentlyViewedServer({ type: 'Projects' }, context),
            getProjectCategoriesRocco(
                { urlInfo: parseURLRocco('/projects') },
                context,
            ),
            fetchGetMatchedStyleImages(context),
            getDirectoryFeaturedProjects(context),
        ]);

        // Featured projects scoped to the current category, matching the
        // legacy grid: the root page counts every pin, a subcategory only the
        // pins whose project categories sit under it.
        const browseBasePath = parseURL(new URL(request.url).pathname).basePath;
        const allBrowseFeaturedProjects =
            featuredProjectsResult &&
            'directoryFeaturedProjects' in featuredProjectsResult
                ? featuredProjectsResult.directoryFeaturedProjects
                : [];
        const isRootBrowsePage =
            browseBasePath === '/projects' || browseBasePath === '/projects/';
        const scopedFeaturedProjects = isRootBrowsePage
            ? allBrowseFeaturedProjects
            : allBrowseFeaturedProjects.filter((fp) =>
                  fp.Project?.Categories?.some((cat: { Link: string }) =>
                      cat.Link?.toLowerCase().startsWith(
                          browseBasePath.toLowerCase(),
                      ),
                  ),
              );
        const featuredProjectIds = scopedFeaturedProjects.map(
            (fp) => fp.ProjectID,
        );

        // Pinned projects lead the grid, matching the legacy page: on the
        // default Featured sort they occupy the top slots in a shuffled order
        // (so no single pin monopolises the opening tile). Pins already in
        // the first search page move to the front; pins it didn't return are
        // injected ahead of it. Deep links past page 1 keep the natural
        // order — pins would land mid-list, not on top. View More appends
        // can't re-add a moved pin: the hook drops already-loaded ids.
        let browseGridTiles = browseData.browse.gridTiles;
        const isFeaturedSort =
            (browseData.browse.sortBy ?? 'featured') === 'featured';
        if (
            isFeaturedSort &&
            browseData.browse.firstLoadedPage === 1 &&
            scopedFeaturedProjects.length > 0
        ) {
            const tilesById = new Map(
                browseGridTiles.map((tile) => [tile.id, tile]),
            );
            const pinnedIds = new Set(featuredProjectIds);
            const pinnedTiles = shuffleArray([...scopedFeaturedProjects])
                .map((fp) => {
                    const inResults = tilesById.get(fp.ProjectID);
                    if (inResults) return inResults;
                    if (!fp.Project) return null;
                    return mapProjectToTileViewModel(
                        fp.Project,
                        {
                            appDisplayMode: context.appDisplayMode,
                            requireStaffAdmin: true,
                        },
                        undefined,
                        context.user,
                    );
                })
                .filter((tile) => tile !== null);
            browseGridTiles = [
                ...pinnedTiles,
                ...browseGridTiles.filter((tile) => !pinnedIds.has(tile.id)),
            ];
        }
        // The projects browse page is dark (#181818), so the site header runs
        // transparent with white text over it — same treatment as the project
        // detail page's hero. Only the revamp (dark) branch opts in; the legacy
        // light page keeps its default dark-text header.
        return {
            ...browseData,
            browse: { ...browseData.browse, gridTiles: browseGridTiles },
            pageTheme: 'transparent' as const,
            recentlyViewedItems:
                recentlyViewedResult?.RecentlyViewedProjects?.map((project) =>
                    mapProjectToTileViewModel(project, {
                        appDisplayMode: context.appDisplayMode,
                        imageToDisplay: 'featured',
                    }),
                ) ?? [],
            rootCategories: mapCategoriesToTileViewModel(
                rootProjectCategoriesResult?.Categories?.edges ?? [],
            ),
            parsedFooterContent: parseFooterContentToFAQ(
                browseData.directoryPageMeta.footerContent ?? '',
            ),
            getMatchedStyleImages,
            featuredProjectIds,
        } as unknown as never;
    }

    const { appDisplayMode, user, featureFlags } = context;
    const isArchiproStaff = getIsArchiproStaffFromMember(user);
    const hideProfessionalsFilter = shouldHideUsProjectProfessionalsFilter({
        countryCode: context.localisation.country,
        isArchiproStaff,
    });

    if (hideProfessionalsFilter) {
        const cleanedUrl = getRedirectUrlWithoutProjectProfessionalsFilter(
            request.url,
        );
        if (cleanedUrl) {
            return redirect(cleanedUrl);
        }
    }

    const isMobile = appDisplayMode === 'mobile';
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    const pageParam = Number(searchParams.get('page'));
    const page = pageParam && isFinite(pageParam) ? pageParam : 1;
    const { basePath } = parseURL(url.pathname);
    const session = await getSession(request);
    const hasFilters = getUrlHasFilters(url);

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'project',
            'directory',
            { pageSize },
            DEFAULT_SORT,
        );
        if (hideProfessionalsFilter) {
            stripProjectProfessionalsFromSearchFilters(queryParams.filters);
        }
        const isFoo = context.config.search.domain === 'foo';
        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });
        // The grid is the page: a Search Service failure is its 503, not
        // "0 projects found".
        const searchPromise = !isFoo
            ? querySearchServiceSearch({ context, queryParams, required: true })
            : null;
        const aggregationPromise = !isFoo
            ? querySearchServiceAggregate({
                  context,
                  queryParams: aggregationProps,
              })
            : null;

        return Promise.all([
            queryParams,
            aggregationProps,
            searchPromise,
            aggregationPromise,
        ]);
    };

    const getAdsGridPotmsAndEsResults = async () => {
        const [ads, sponsoredAds, gridPotms] = await Promise.all([
            adsRequest({
                type: 'DirectoryWithProjectPlacementGroup',
                requestUrl: request.url,
                ...context,
                request,
            }),
            adRequest({
                type: 'SponsorshipDirectoryPlacement',
                requestUrl: request.url,
                ...context,
                request,
            }),
            getGridPotms(context),
        ]);
        const validVerticalAds = ads?.VerticalTilePlacement?.filter(
            (ad) => !!ad?.id,
        );
        const queryESPageSize = getProjectsQuerySize({
            sponsoredAdsCount: sponsoredAds?.length ?? 0,
            gridPotmCount: gridPotms?.length ?? 0,
            verticalAdsCount: validVerticalAds?.length ?? 0,
            hasFilters,
            page,
            config: {
                defaultProjectPageSize: DEFAULT_PROJECT_PAGE_SIZE,
                maxVerticalTilesPerPage: MAX_VERTICAL_TILES_PER_PAGE,
                maxVerticalAdTilesPerPage: MAX_VERTICAL_AD_TILES_PER_PAGE,
                maxSponsoredAdTilesDisplayed: MAX_SPONSORED_AD_TILES_DISPLAYED,
            },
        });
        return {
            ads: {
                ...ads,
                VerticalTilePlacement: validVerticalAds,
            } as AdsLoaderData,
            sponsoredAds,
            gridPotms,
            query: await queryES(queryESPageSize),
        };
    };

    const [
        // Single combined call replaces the separate getDirectoryPageMeta + getPageLocalisation pair
        // (both previously hit the getDirectoryPageMeta resolver with identical variables).
        combinedPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        projectCategory,
        recentlyViewedResult,
        heroPotm,
        activeDirectoryCategoriesCount,
        featuredProjectsResult,
        {
            ads,
            sponsoredAds,
            gridPotms,
            query: [queryParams, aggregationProps, searchResult, aggregations],
        },
        rootProjectCategoriesResult,
    ] = await Promise.all([
        getDirectoryPageMetaCombined({ context, request }),
        // fetch the root projects category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/projects', request.url),
            ...context,
        }),
        getProjectCategories({
            ...context,
            request,
        }),
        getRecentlyViewedServer({ type: 'Projects' }, context),
        getHeroPotm(context),
        getActiveDirectoryCategoriesCount({}, context),
        getDirectoryFeaturedProjects(context),
        getAdsGridPotmsAndEsResults(),
        // Fetch root-level project categories for ExploreAllCategories (desktop only)
        !isMobile
            ? getProjectCategoriesRocco(
                  { urlInfo: parseURLRocco('/projects') },
                  context,
              )
            : null,
    ]);

    const { directoryPageMeta, localisation } = combinedPageMeta;

    const contentGate: ContentGateState = await resolveContentGate({
        context,
        request,
        surface: 'projects',
        categoryEnabled: localisation?.ContentGateEnabled ?? false,
        resultCount: searchResult?.nbHits ?? 0,
    });

    // A gated visitor gets the first page and nothing past it, so a deep-linked
    // `?page=N` has no page to land on. Send them to the one the gate does cover.
    if (contentGate.gated && Number(url.searchParams.get('page') ?? '1') > 1) {
        const gateParams = new URLSearchParams(url.search);
        gateParams.delete('page');
        const gateSearch = gateParams.toString();
        throw redirect(
            gateSearch ? `${url.pathname}?${gateSearch}` : url.pathname,
        );
    }

    const _disableHeroPotm = true;
    const hasHeroPotmMedia =
        !_disableHeroPotm &&
        (isMobile
            ? !!(
                  heroPotm?.videoCoverMobile &&
                  (heroPotm?.mobileVideoMp4 || heroPotm?.mobileVideoWebm)
              )
            : !!(
                  heroPotm?.videoCoverDesktop &&
                  (heroPotm?.teaserVideoMp4 || heroPotm?.teaserVideoWebm)
              ));

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PROJECT_PAGE_SIZE,
    });

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'project',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: queryParams.sortBy ?? null,
              defaultSortBy: DEFAULT_SORT,
          })
        : createEmptyFilterStates({
              visibilityType: 'directory',
              defaultSortBy: DEFAULT_SORT,
          });

    const appliedFilterCount = getAppliedFilterNumber(filterStates);

    // Determine if preloading is enabled (no ads/banners at top of grid)
    // We use the same condition for setting eager loading on tiles
    const hasCategoryPlacementAds = Boolean(ads?.CategoryPlacement?.length);
    const hasSponsoredAds = Boolean(sponsoredAds?.length);
    const shouldPreloadImages =
        appliedFilterCount > 0 ||
        (!hasCategoryPlacementAds && !hasSponsoredAds);
    const numberTilesToEagerLoad = !isMobile ? 3 : 2;

    // For high admins (BC-4449 rollout), fetch banner data from the live DB so
    // tiles can show which slide is the banner and carry the per-slide
    // LibraryImage IDs needed to set one.
    const isHighAdmin =
        user?.__typename === 'Me' &&
        'IsHighAdmin' in user &&
        user.IsHighAdmin === true;
    // The tile→hero morph (revamp_2026) needs slide 0 to BE the
    // hero's TopBanner: the ES TopBanner.Thumb string-match silently fails
    // when the banner image is a copy with its own file, leaving the tile on
    // an arbitrary ES slide. While transitions are on, fetch the ID-based
    // banner data for everyone (one batched query per page).
    const detailsTransitionActive =
        hasFeature(context.featureFlags, 'revamp_2026') || import.meta.env.DEV;
    const bannerInfoWanted =
        detailsTransitionActive ||
        (isHighAdmin &&
            hasFeature(
                context.featureFlags,
                PROJECT_TILE_BANNER_SELECTOR_FLAG,
            ));
    const projectHitIds =
        searchResult?.hits
            ?.filter((hit) => hit.PType === 'Project')
            .map((hit) => hit.id) ?? [];

    // Both augments key off the same hit ids, so they go out together — the
    // grid pays for the slower of the two, not their sum.
    const [bannerInfoByProject, websiteByProject] = await Promise.all([
        bannerInfoWanted && projectHitIds.length
            ? getProjectBannerImages({ ids: projectHitIds }, context).catch(
                  (error: unknown) => {
                      context.logger.warning(
                          `Failed to fetch project banner images: ${String(error)}`,
                      );
                      return undefined;
                  },
              )
            : undefined,
        fetchTileWebsites(projectHitIds, 'projects', context),
    ]);

    const projects = withTileWebsites(
        getProjectsFromESSearchResults(searchResult, user, bannerInfoByProject),
        websiteByProject,
    );
    const tiles: TileViewModel<ProjectTileViewModelRocco>[] = projects.map(
        (project, index) => ({
            aspectRatio: 'default',
            model: project,
            // Set eager loading for first N tiles to match preloading
            eager: shouldPreloadImages && index < numberTilesToEagerLoad,
        }),
    );

    const projectTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Project')
                tiles.push(
                    mapESProjectTileViewModel(hit, 'medium', isMobile, user),
                );
            return tiles;
        }, [] as ProjectTileViewModel[]) ?? [];

    // Extract featured project IDs from query result
    const allFeaturedProjects =
        featuredProjectsResult &&
        'directoryFeaturedProjects' in featuredProjectsResult
            ? featuredProjectsResult.directoryFeaturedProjects
            : [];

    // Filter featured projects by current category path.
    // On root /projects page, show all featured projects.
    // On subcategory pages (e.g. /projects/commercial), only show featured
    // projects whose categories include the current path.
    const isRootProjectsPage =
        basePath === '/projects' || basePath === '/projects/';
    // Pinned projects always occupy the top slots but in a random order on
    // each page load, so no single pinned project monopolises the first tile
    const filteredFeaturedProjects = shuffleArray(
        isRootProjectsPage
            ? [...allFeaturedProjects]
            : allFeaturedProjects.filter((fp) =>
                  fp.Project?.Categories?.some((cat: { Link: string }) =>
                      cat.Link?.toLowerCase().startsWith(
                          basePath.toLowerCase(),
                      ),
                  ),
              ),
    );
    const featuredProjectIds = filteredFeaturedProjects.map(
        (fp) => fp.ProjectID,
    );

    // Pinned projects only take priority on the Featured (default) sort. For
    // any other sort (New, Trending, …) they sort naturally with the results.
    const isFeaturedSort =
        (queryParams.sortBy ?? DEFAULT_SORT) === DEFAULT_SORT;

    // If on page 1, inject any featured projects that aren't in the current
    // ES results so buildProjectTileGrid can move them to the front.
    if (page === 1 && isFeaturedSort) {
        // Process in reverse so the first featured project ends up at index 0
        for (let i = filteredFeaturedProjects.length - 1; i >= 0; i--) {
            const fp = filteredFeaturedProjects[i];
            if (!fp?.Project) continue;
            const isInResults = tiles.some(
                (tile) => tile.model.id === fp.ProjectID,
            );
            if (!isInResults) {
                const featuredTileModel = mapProjectToTileViewModel(
                    fp.Project,
                    { appDisplayMode, requireStaffAdmin: true },
                    undefined,
                    user,
                );
                tiles.unshift({
                    aspectRatio: 'default',
                    model: featuredTileModel,
                });
            }
        }
    }

    const shuffledAds = ads?.CategoryPlacement
        ? shuffleArray(ads.CategoryPlacement)
        : [];

    const verticalTileAds = ads?.VerticalTilePlacement
        ? shuffleArray(ads.VerticalTilePlacement)
        : [];
    const sponsoredAdsShuffled = sponsoredAds
        ? professionalEvenShuffleAds(sponsoredAds).slice(
              0,
              MAX_SPONSORED_AD_TILES_DISPLAYED,
          )
        : [];

    const sponsoredAdsTiles = sponsoredAdsShuffled.flatMap((advert) => {
        const professionalTile = advertCreativeProjectTileView(advert);
        return professionalTile !== null ? professionalTile : [];
    });

    const projectAdsTiles = ads ? getProjectPlacements(ads, isMobile) : [];

    const enableAwardBadges = hasFeature(featureFlags, 'award_badges');

    return {
        userPreferenceSession: session.getUserPreferences(),
        appDisplayMode,
        directoryPageMeta,
        // Parse the footer FAQ on the server so SSR and client render the same grid
        // markup. The component's client fallback parser is DOM-only and produces a
        // different node tree, which breaks hydration of the whole document.
        parsedFooterContent: parseFooterContentToFAQ(
            directoryPageMeta.footerContent,
        ),
        projectCategory,
        projectTiles,
        pagination,
        // Trimmed too: the tile lists are derived from this, but it travels in
        // the payload itself, so leaving it whole would put the withheld
        // records back in the document.
        searchResult: applyContentGateToHits(searchResult, contentGate),
        recentlyViewedItems:
            recentlyViewedResult?.RecentlyViewedProjects?.map((project) =>
                mapProjectToTileViewModel(project, {
                    appDisplayMode,
                    imageToDisplay: 'featured',
                }),
            ) ?? [],
        filterStates,
        // If directory doesn't have a description, use the root category description as a fallback
        categoryDescription:
            directoryPageMeta?.directoryHeaderDescription ||
            rootDirectoryPageMeta?.directoryHeaderDescription ||
            `ArchiPro features the largest selection of completed architectural projects in ${fullCountryNameFromCode(context.localisation.country)}. These building and renovation accomplishments showcase the very best in design ingenuity and talent. Browse through these inspirational projects and connect with the professionals involved directly via ArchiPro.`,
        ads: shuffledAds,
        verticalTileAds,
        sponsoredAds: sponsoredAdsTiles,
        projectAds: projectAdsTiles.slice(0, MAX_ADS_PER_PAGE),
        localisation,
        enableAwardBadges,
        appliedFilterCount,
        url: url.pathname + url.search,
        heroPotm: _disableHeroPotm ? undefined : heroPotm,
        gridPotms,
        hasHeroPotmMedia: hasHeroPotmMedia,
        pageTheme: hasHeroPotmMedia ? 'transparent' : 'default',
        // Trimmed before it leaves the server: the tiles past the cut-off are
        // not the visitor's to see, so they never reach the document.
        projectTilesRocco: applyContentGate(tiles, contentGate),
        contentGate,
        searchIslandPlaceholders: mapCategoryCountToPlaceholder(
            activeDirectoryCategoriesCount?.projects ?? [],
            `Search projects`,
            'projects',
        ),
        featuredProjectIds,
        isFeaturedSort,
        rootCategories: mapCategoriesToTileViewModel(
            rootProjectCategoriesResult?.Categories?.edges ?? [],
        ),
        hideProfessionalsFilter,
    };
};

const ProjectsCategoryPage = () => {
    const { user } = useAppData();
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const { isSafari } = useDeviceBrowserContext();
    const filtersRef = useRef<HTMLDivElement | null>(null);
    const {
        categoryDescription,
        directoryPageMeta,
        projectTiles,
        pagination,
        recentlyViewedItems,
        filterStates,
        ads,
        verticalTileAds,
        sponsoredAds,
        projectAds,
        enableAwardBadges,
        appliedFilterCount,
        heroPotm,
        gridPotms,
        hasHeroPotmMedia,
        projectTilesRocco,
        featuredProjectIds: loaderFeaturedProjectIds,
        isFeaturedSort,
        rootCategories,
        hideProfessionalsFilter,
        parsedFooterContent,
        contentGate,
    } = useLoaderData<typeof loader>();
    const tracker = useTracker();
    const [searchParams] = useSearchParams();
    const { setCategory } = useCurrentCategory();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads);
    const verticalTileAdsMemoized = useLocationMemo(verticalTileAds);
    const sponsoredAdsMemoized = useLocationMemo(sponsoredAds);
    const projectAdsMemoized = useLocationMemo(projectAds);
    const remixLocation = useLocation();
    const projectTilesRoccoAdmin = useTilesAdminData(
        projectTilesRocco as TileViewModel<ProjectTileViewModelRocco>[],
    );

    // Featured projects state + hook
    const [optimisticFeaturedIds, setOptimisticFeaturedIds] = useState<
        number[]
    >(loaderFeaturedProjectIds);
    // Track tiles currently fading out (being unfeatured)
    const [unfeaturedIds, setUnfeaturedIds] = useState<Set<number>>(new Set());
    const unfeaturedTimersRef = useRef<
        Map<number, ReturnType<typeof setTimeout>>
    >(new Map());

    const {
        setInLibrary,
        handleRemoveFromLibrary,
        removedFromLibraryIds,
        unmountedFromLibraryIds,
    } = useLibraryRemovalUi({
        // The server auto-unpins projects leaving the directory — mirror it on
        // confirmed removals so the tile (and its undo-restored ghost) doesn't
        // keep a stale pinned state. Failed removals leave pin state untouched.
        onAfterRemove: (itemType, itemId) => {
            if (itemType === 'project') {
                setOptimisticFeaturedIds((prev) =>
                    prev.filter((id) => id !== itemId),
                );
            }
        },
    });

    // Sync optimistic state when loader data changes (navigation / revalidation)
    useEffect(() => {
        setOptimisticFeaturedIds(loaderFeaturedProjectIds);
    }, [loaderFeaturedProjectIds]);

    // Cleanup unfeature timers on unmount (library-removal timers handled by useLibraryRemovalUi)
    useEffect(() => {
        const timers = unfeaturedTimersRef.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    const { toggleFeaturedProject } = useToggleFeaturedProject(
        // onError: revert optimistic update and clear any fade-out state
        (error) => {
            setOptimisticFeaturedIds(loaderFeaturedProjectIds);
            setUnfeaturedIds(new Set());
            unfeaturedTimersRef.current.forEach((timer) => clearTimeout(timer));
            unfeaturedTimersRef.current.clear();
            if (error.Message) {
                toast.error(error.Message);
            }
        },
        // onSuccess: show toast
        (_projectId, featured) => {
            toast.success(
                featured
                    ? 'Project pinned to top of directory'
                    : 'Project unpinned from directory',
            );
        },
    );

    // Track tiles that were just pinned (for fade-in animation)
    const [newlyFeaturedIds, setNewlyFeaturedIds] = useState<Set<number>>(
        new Set(),
    );

    const ANIMATION_DURATION = 500;

    const handleToggleFeature = useCallback(
        (projectId: number, feature: boolean) => {
            if (feature) {
                // Pin: add to featured + mark as "newly featured" for fade-in
                setNewlyFeaturedIds((prev) => new Set(prev).add(projectId));
                setOptimisticFeaturedIds((prev) => [projectId, ...prev]);
                // On next frame, remove from newlyFeaturedIds so CSS transitions to visible
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setNewlyFeaturedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(projectId);
                            return next;
                        });
                    });
                });
            } else {
                // Unpin: two-phase — fade out first, then remove
                setUnfeaturedIds((prev) => new Set(prev).add(projectId));

                const timer = setTimeout(() => {
                    setOptimisticFeaturedIds((prev) =>
                        prev.filter((id) => id !== projectId),
                    );
                    setUnfeaturedIds((prev) => {
                        const next = new Set(prev);
                        next.delete(projectId);
                        return next;
                    });
                    unfeaturedTimersRef.current.delete(projectId);
                }, ANIMATION_DURATION);

                unfeaturedTimersRef.current.set(projectId, timer);
            }
            toggleFeaturedProject?.(projectId, feature);
        },
        [toggleFeaturedProject],
    );

    // init category tiles tracking
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const projectsRef = useRef<HTMLDivElement>(null);
    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedRequirements = searchParams.getAll('services').join(' | ');
    const selectedProfessions = searchParams
        .getAll('professionals')
        .join(' | ');
    useEffect(() => {
        tracker.log('projectListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                requirement: selectedRequirements,
                professionals: selectedProfessions,
            },
        });
    }, [selectedCategory, selectedProfessions, selectedRequirements, tracker]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const projectIDs = projectTiles.map((project) => project.tile.projectID);
    const sponsoredAdsIDs = sponsoredAdsMemoized.map(
        (project) => project.projectId,
    );

    const {
        getTileTopActions,
        hotButtonModal,
        getTileAdminCaption,
        canEditAdmin,
    } = useTileHotButtons({
        itemType: 'project',
        itemIDs: [...projectIDs, ...sponsoredAdsIDs],
    });

    const isStaffUser = user.__typename === 'Me' && user.IsArchiproStaff;
    const showViewCount = useABTestIsOn('project_tile_stats') && isStaffUser;

    //potms feature flag conditions
    const directoryPotmEnbaled = useABTestIsOn('directory-potms');
    const potmFeatureOn = usePotmHeroEnabled();
    const heroPotmsEnabled = potmFeatureOn && heroPotm;

    // check if there are both cover image and videos available
    const showHeroPotm = !!heroPotmsEnabled && hasHeroPotmMedia;

    const searchFilters = useSearchFilters({
        category: 'project',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: getProjectCategoryIgnoreFilterKeys(
            hideProfessionalsFilter,
        ),
    });

    const apConnectBannerFlag = useFeature('archipro_connect_category_banner');

    const showProfessionalArchiproBanner =
        getShowProfessionalArchiproConnectBanner(user);

    // Drop tiles whose fade-out animation has finished so the grid reflows
    // and fills the gap left by removed-from-library tiles.
    const visibleProjectTilesRocco = useMemo(
        () =>
            projectTilesRoccoAdmin.filter(
                (t) => !unmountedFromLibraryIds.has(t.model.id),
            ),
        [projectTilesRoccoAdmin, unmountedFromLibraryIds],
    );

    const sortedGridTiles = buildProjectTileGrid({
        showArchiproConnectBanner:
            showProfessionalArchiproBanner && apConnectBannerFlag,
        desktop,
        ads: adsMemoized,
        verticalTileAds: verticalTileAdsMemoized,
        sponsoredAds: sponsoredAdsMemoized,
        projectAds: projectAdsMemoized,
        potmFeatureOn: directoryPotmEnbaled,
        projectTiles: visibleProjectTilesRocco,
        gridPotms,
        appliedFilterCount,
        pagination,
        columnCount: desktop ? (filterDrawerOpen ? 3 : 4) : 1,
        featuredProjectIds:
            pagination.page === 1 && isFeaturedSort
                ? optimisticFeaturedIds
                : [],
    });

    const tileGridItems: ReactElement[] = renderProjectTileGrid({
        tiles: sortedGridTiles,
        mobile,
        getTileTopActions,
        willDelete,
        getTileAdminCaption,
        filterDrawerOpen,
        desktop,
        showViewCount,
        enableAwardBadges,
        canEditAdmin,
        featuredProjectIds: pagination.page === 1 ? optimisticFeaturedIds : [],
        unfeaturedIds,
        newlyFeaturedIds,
        removedFromLibraryIds,
        onToggleFeature: toggleFeaturedProject
            ? handleToggleFeature
            : undefined,
        onRemoveFromLibrary: setInLibrary ? handleRemoveFromLibrary : undefined,
    }) as ReactElement[];

    useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <Box>
            {showHeroPotm &&
                (desktop ? (
                    <DirectoryHeroPotmDesktop
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ) : (
                    <DirectoryHeroPotmMobile
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ))}
            <Container
                ref={projectsRef}
                className={css(S.ProjectCategoryContainer)}
            >
                {desktop ? (
                    <TitleSectionDesktop
                        category={directoryPageMeta.category}
                        rootCategory="Projects."
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates.totalCount}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        showCategoryTitle={!heroPotmsEnabled}
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.category}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                        showCategoryTitle={!showHeroPotm}
                    >
                        <TitleSectionFilterMobile
                            esCategory="project"
                            searchFilters={searchFilters}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="project"
                        searchFilters={searchFilters}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        filterDrawerOpen={filterDrawerOpen}
                        componentBefore={
                            <div ref={filtersRef}>
                                <CollapsibleToggle
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                />
                            </div>
                        }
                    />
                )}

                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="project"
                                searchFilters={searchFilters}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={false}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                ['category'],
                                            ),
                                        )
                                    }
                                />
                            )}

                        {sortedGridTiles.length ? (
                            <VirtualizedGrid
                                items={tileGridItems}
                                renderItem={(item: React.ReactNode) => item}
                                className={cn(
                                    'grid transition-all duration-500 ease-in-out',
                                    filterDrawerOpen
                                        ? 'grid-cols-1 md:grid-cols-3'
                                        : 'grid-cols-1 md:grid-cols-4',
                                    'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20',
                                )}
                                enabled={isSafari}
                                initialVisibleCount={15}
                                batchSize={10}
                                loadMoreThreshold={0.7}
                                reduceThreshold={0.3}
                                minVisibleCount={15}
                                startMarker={
                                    <div className="hidden">
                                        <CollapsibleToggle
                                            filterDrawerOpen={filterDrawerOpen}
                                            toggleFilterDrawer={
                                                toggleFilterDrawer
                                            }
                                        />
                                    </div>
                                }
                                endMarker={
                                    contentGate.gated ? (
                                        <ContentGatePanel
                                            totalCount={pagination.totalCount}
                                            visibleTiles={
                                                contentGate.visibleTiles
                                            }
                                            entityNoun="projects"
                                        />
                                    ) : (
                                        pagination.totalPages > 1 && (
                                            <div className="pt-26.25 md:pt-20">
                                                <TilePagination
                                                    currentPath={remixLocation}
                                                    current={pagination.page}
                                                    total={
                                                        pagination.totalPages
                                                    }
                                                    urlBuilder={
                                                        buildPaginationUrl
                                                    }
                                                    visibleCount={
                                                        appDisplayMode.desktop
                                                            ? 4
                                                            : 3
                                                    }
                                                />
                                            </div>
                                        )
                                    )
                                }
                            />
                        ) : (
                            <NoResultsSection resultsType={'projects'} />
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="my-10 flex flex-col gap-10 md:mt-17.5 md:mb-20 md:gap-17.5">
                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedItems}
                    />

                    {desktop && (
                        <ExploreAllCategories
                            categories={rootCategories}
                            title="Explore all project categories"
                        />
                    )}

                    <CategoryPageFAQ
                        faqSchemas={directoryPageMeta.faqSchemas}
                        footerContent={directoryPageMeta.footerContent}
                        parsedFooterContent={parsedFooterContent}
                        variant={desktop ? 'grid' : undefined}
                        categoryLabel={
                            desktop ? 'ArchiPro Projects' : undefined
                        }
                    />
                </div>

                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete project'}
                        content={
                            'Are you sure you would like to delete this project? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}
                {hotButtonModal}
            </Container>
        </Box>
    );
};

/**
 * Projects carousel browse page (behind the category_pages_revamp flag). Builds
 * the shared filter bar from the projects filter config + the loader's Rocco
 * filter states, then renders it in DirectoryBrowsePage.
 */
/** Browse loader payload + the below-grid content ported from legacy projects. */
type ProjectsBrowseLoaderData = DirectoryBrowseLoaderData & {
    recentlyViewedItems: ReturnType<typeof mapProjectToTileViewModel>[];
    rootCategories: ReturnType<typeof mapCategoriesToTileViewModel>;
    parsedFooterContent: ReturnType<typeof parseFooterContentToFAQ>;
    getMatchedStyleImages: GetMatchedStyleImage[];
    /**
     * Projects pinned to the top of this directory category (empty for
     * non-admin visitors — the loader only fetches the set for admins).
     */
    featuredProjectIds: number[];
};

const ProjectsDirectoryBrowse = () => {
    const [browseSearchParams] = useSearchParams();
    const data = useLoaderData<
        typeof loader
    >() as unknown as ProjectsBrowseLoaderData;
    // Spaces and Styles are the other two tabs; with their flag off the row
    // would be a lone Projects tab, so the whole row goes.
    const imageLabelBrowseEnabled = useFeature(
        IMAGE_LABEL_DIRECTORY_FEATURE_KEY,
    );
    const { desktop } = useAppDisplayModeContext();

    // The browse list impression the legacy page owned.
    useDirectoryListImpression({
        event: 'projectListImpression',
        source: 'projects',
        dimensions: {
            category: getDirectoryFullName(data.directoryPageMeta),
            requirement: browseSearchParams.getAll('services').join(' | '),
            professionals: browseSearchParams
                .getAll('professionals')
                .join(' | '),
        },
    });
    const filterSortProps = useCategoryFilterSort(
        PROJECT_FILTERS_CONFIG,
        SearchIndexes.Projects,
        data.filterStates,
        data.browse.defaultSort,
    );

    // Inject the shared tile actions (click/hover/slide tracking plus the
    // flag-gated high-admin set-banner action) so project tiles show the
    // per-slide banner checkbox, as on the legacy grid.
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    // Optimistic pin state: the context-menu label flips at once, and the
    // loader's set re-syncs it on navigation / revalidation.
    const [optimisticFeaturedIds, setOptimisticFeaturedIds] = useState<
        number[]
    >(data.featuredProjectIds ?? []);
    useEffect(() => {
        setOptimisticFeaturedIds(data.featuredProjectIds ?? []);
    }, [data.featuredProjectIds]);

    const {
        setInLibrary,
        handleRemoveFromLibrary,
        removedFromLibraryIds,
        unmountedFromLibraryIds,
    } = useLibraryRemovalUi({
        // The server auto-unpins projects leaving the directory — mirror it on
        // confirmed removals so a restored tile doesn't keep a stale pinned
        // state. Failed removals leave pin state untouched.
        onAfterRemove: (itemType, itemId) => {
            if (itemType === 'project') {
                setOptimisticFeaturedIds((prev) =>
                    prev.filter((id) => id !== itemId),
                );
            }
        },
    });

    const { toggleFeaturedProject } = useToggleFeaturedProject(
        // onError: revert the optimistic pin state
        (error) => {
            setOptimisticFeaturedIds(data.featuredProjectIds ?? []);
            if (error.Message) {
                toast.error(error.Message);
            }
        },
        // onSuccess: show toast
        (_projectId, featured) => {
            toast.success(
                featured
                    ? 'Project pinned to top of directory'
                    : 'Project unpinned from directory',
            );
        },
    );

    const handleToggleFeature = useCallback(
        (projectId: number, feature: boolean) => {
            setOptimisticFeaturedIds((prev) =>
                feature
                    ? [projectId, ...prev.filter((id) => id !== projectId)]
                    : prev.filter((id) => id !== projectId),
            );
            toggleFeaturedProject?.(projectId, feature);
        },
        [toggleFeaturedProject],
    );
    // Display dropdown: user-selected desktop grid columns for this vertical.
    const { columns: displayColumns, setColumns: setDisplayColumns } =
        useDirectoryDisplayColumns('projects', 3);
    const { browse, contentGate } = data;
    const activeCategory =
        data.breadcrumbs.length > 1
            ? data.breadcrumbs[data.breadcrumbs.length - 1]?.label
            : undefined;
    const sectionTitle = activeCategory
        ? `All ${activeCategory} Projects`
        : 'All Projects';
    const childPills = browse.categoryCards.map((category) => ({
        id: category.id,
        title: category.title,
        link: category.link,
        // Sidebar-only: gates the drill-in chevron; the pill bar ignores it.
        hasChildren: category.subCategories.length > 0,
    }));
    const categoryPills = activeCategory
        ? childPills
        : [{ id: 0, title: 'All Projects', link: '/projects' }, ...childPills];
    return (
        <>
            {/* Listener for the intro / grid-injection Get Matched buttons
                (shared jotai atom). Ships with the revamp flag that gates this
                whole component — no separate Get Matched flag. From /projects
                the brief knows which flow to run. */}
            <GetMatchedHost
                enquiryType="professional"
                styleImages={data.getMatchedStyleImages}
            />
            <DirectoryBrowsePage
                entityType="projects"
                theme="dark"
                intro={
                    <>
                        {/* Below md only, as the products directory opens:
                            the mark in the corner the display heading used
                            to take, white on this page's dark ground. */}
                        <FadingLogomark tone="dark" />
                        <DirectoryIntro
                            eyebrow={data.eyebrow}
                            heading={data.heading}
                            categoryPrefix={activeCategory}
                            // The products directory's phone opening: no
                            // display heading, and the description as one
                            // block of running text in one ink.
                            hideMobileHeading
                            mobileSingleTone
                            statementClassName="max-md:mt-5 max-md:text-base max-md:font-medium"
                            entryPoint="CategoryLead"
                            explainer={{
                                eyebrow: 'Get Matched',
                                title: 'Get Matched with the right professionals',
                                body: 'Seen a project you like? Give us a short brief on yours: where it is, what it is, who you want to work with, your budget and timeline. Our team reads it and personally matches you with two or three professionals whose work, availability and approach fit.',
                            }}
                        />
                    </>
                }
                categoryCarousel={
                    // Off, matching products: the pills and grid sit straight
                    // under the description. The component and its data are
                    // left in place — restoring it is putting the
                    // DirectoryCategoryCarousel back here, gated on
                    // `!activeCategory && browse.categoryCards.length > 0` as
                    // before (root only; a category page already lists its
                    // children as pills).
                    null
                }
                pillBar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryPillBar
                            viewSwitch={
                                imageLabelBrowseEnabled ? (
                                    <ProjectViewPills
                                        activeView="projects"
                                        isDark
                                    />
                                ) : undefined
                            }
                            breadcrumbs={data.breadcrumbs}
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            prefetchUrl={filterSortProps.settledDestinationURL}
                            sectionTitle={sectionTitle}
                            categoryPills={categoryPills}
                            rootPath="/projects"
                            rootLabel="All Projects"
                            searchPlaceholder="Search Projects..."
                            displayColumns={displayColumns}
                            onDisplayColumnsChange={setDisplayColumns}
                        />
                    ) : null
                }
                sidebar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryFilterSidebar
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            // Categories are deliberately not passed: the pill
                            // bar above the grid already lists them and carries
                            // the Back control, and repeating them here pushed
                            // the facets down the panel.
                        />
                    ) : null
                }
                rows={
                    <DirectoryBrowseGrid
                        entityType="projects"
                        columns={displayColumns}
                        entityNoun="projects"
                        aspectRatio="projectBrowse"
                        categoryLink={browse.categoryLink}
                        initialTiles={browse.gridTiles}
                        totalCount={browse.gridTotalCount}
                        filters={browse.filters}
                        search={browse.search}
                        sortBy={browse.sortBy ?? undefined}
                        firstLoadedPage={browse.firstLoadedPage}
                        requestedPage={browse.requestedPage}
                        contentGate={contentGate}
                        activeCategoryLabel={activeCategory}
                        featuredProjectIds={optimisticFeaturedIds}
                        removedFromLibraryIds={removedFromLibraryIds}
                        unmountedFromLibraryIds={unmountedFromLibraryIds}
                        onToggleFeature={
                            toggleFeaturedProject
                                ? handleToggleFeature
                                : undefined
                        }
                        onRemoveFromLibrary={
                            setInLibrary ? handleRemoveFromLibrary : undefined
                        }
                    />
                }
                belowGrid={
                    // Legacy order: recently viewed → explore all categories →
                    // newspaper FAQ. Components flip to light text on the dark theme.
                    // ExploreAllCategories renders unconditionally (was previously
                    // desktop-only) — its own per-group accordion now handles the
                    // mobile collapse, so the category links stay in the DOM below
                    // md instead of disappearing entirely.
                    <>
                        {/* mb matches the professionals directory's gap-10
                            below its recently-viewed strip; desktop keeps its
                            existing rhythm. Guarded on items so a reader with
                            no history (e.g. logged out) gets no stray gap. */}
                        {data.recentlyViewedItems.length > 0 && (
                            <div className="mb-[6.25rem] md:mb-0">
                                <CategoryPageRecentlyViewed
                                    displayMode={desktop ? 'desktop' : 'mobile'}
                                    items={data.recentlyViewedItems}
                                />
                            </div>
                        )}
                        <ExploreAllCategories
                            categories={data.rootCategories}
                            title="Explore all project categories"
                        />
                        <CategoryPageFAQ
                            faqSchemas={data.directoryPageMeta.faqSchemas}
                            footerContent={data.directoryPageMeta.footerContent}
                            parsedFooterContent={data.parsedFooterContent}
                            variant={desktop ? 'grid' : undefined}
                            categoryLabel={
                                desktop ? 'ArchiPro Projects' : undefined
                            }
                        />
                    </>
                }
            />
        </>
    );
};

const ProjectsRoute = () => {
    const categoryRevamp = useCategoryRevamp();
    if (categoryRevamp) {
        return <ProjectsDirectoryBrowse />;
    }
    return <ProjectsCategoryPage />;
};

/**
 * A listing surface, so everything on this route opens detail pages in the
 * mobile app-mode presentation. The hero morph is not scoped here — it runs
 * wherever a tile does, behind the feature flag alone.
 */
const ProjectsRouteWithTileAppMode = () => (
    <TileAppModeScope>
        <ProjectsRoute />
    </TileAppModeScope>
);

export default ProjectsRouteWithTileAppMode;
